<template>
  <Teleport v-if="toolbarTarget" :to="toolbarTarget">
    <button
      class="ol-btn"
      :class="{ 'ol-btn--active': visible }"
      type="button"
      title="Atalho [L] para alternar as limites abertas no gráfico"
      @click="toggleVisible"
    >
      <span class="shortcut">[L]</span>
      Limites: <strong>{{ visible ? 'ON' : 'OFF' }}</strong>
      <span v-if="visible" class="ol-btn__hint">{{ clusters.length }}</span>
    </button>
  </Teleport>

  <div v-if="visible && ready" ref="overlayRef" class="open-limits__overlay">
    <div
      v-for="row in renderedRows"
      :key="row.key"
      class="ol-row"
      :class="[
        row.side === 'BUY' ? 'ol-row--buy' : 'ol-row--sell',
        row.draggable ? 'ol-row--draggable' : 'ol-row--locked',
        drag && drag.orderId === row.orderId ? 'ol-row--dragging' : '',
      ]"
      :style="{ top: row.y + 'px' }"
    >
      <div
        class="ol-row__line"
        :title="row.draggable ? 'Arraste para alterar o preço' : 'Mais de uma ordem neste preço. Edite no painel.'"
        @pointerdown="onPointerDown($event, row)"
        @pointerenter="onEnter(row)"
        @pointerleave="onLeave($event, row)"
      />
      <div
        class="ol-row__label"
        :style="{ transform: 'translateY(calc(-50% + ' + row.labelShift + 'px))' }"
        :title="row.draggable ? 'Arraste para alterar o preço' : 'Mais de uma ordem neste preço. Edite no painel.'"
        @pointerdown="onPointerDown($event, row)"
        @pointerenter="onEnter(row)"
        @pointerleave="onLeave($event, row)"
      >
        <span>{{ row.side }}</span>
        <template v-if="showQtyEditor(row)">
          <input
            class="ol-qty"
            :value="qtyDraft"
            inputmode="decimal"
            @input="qtyDraft = $event.target.value"
            @pointerdown.stop
            @keydown.enter.prevent="confirmQty(row)"
            @keydown.esc.prevent="focusedKey = null"
            @focus="focusedKey = row.key"
          />
          <button type="button" class="ol-ok" @pointerdown.stop.prevent="confirmQty(row)">OK</button>
        </template>
        <span v-else>{{ formatQty(row.qty) }}</span>
        <span>${{ formatPrice(row.price) }}</span>
        <span v-if="row.count > 1">×{{ row.count }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { clusterOpenLimits, roundLimitPrice, parseLimitQty } from './domain/clusterOpenLimits'

const LABEL_GAP_PX = 18
const PENDING_MS = 8000

// eslint-disable-next-line no-undef -- compiler macro (script setup)
const props = defineProps({
  priceToY: { type: Function, required: true },
  yToPrice: { type: Function, default: null },
  ready: { type: Boolean, default: false },
  toolbarTarget: { type: [Object, String], default: null },
})

const store = useStore()
const visible = ref(true)
const renderedRows = ref([])
const overlayRef = ref(null)
const hoveredKey = ref(null)
const focusedKey = ref(null)
const qtyDraft = ref('')
const drag = ref(null)
const pending = ref(null)
let pendingTimer = null

function toggleVisible() {
  visible.value = !visible.value
}

function handleKeyDown(e) {
  if (e.target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return
  if (e.key === 'l' || e.key === 'L') toggleVisible()
}

const clusters = computed(() => clusterOpenLimits(store.state.terminalOpenOrders))

function formatQty(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return '—'
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 6 })
}

function formatPrice(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return '—'
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function shiftOverlappingLabels(rows) {
  const sorted = [...rows].sort((a, b) => a.y - b.y)
  for (let i = 1; i < sorted.length; i += 1) {
    const prev = sorted[i - 1]
    const curr = sorted[i]
    const minCenter = prev.y + prev.labelShift + LABEL_GAP_PX
    if (curr.y + curr.labelShift < minCenter) {
      curr.labelShift = minCenter - curr.y
    }
  }
  return rows
}

function orderOverride(cluster) {
  const orderId = cluster.orderIds.length === 1 ? cluster.orderIds[0] : null
  if (!orderId) return null
  if (drag.value && drag.value.orderId === orderId) return drag.value
  if (pending.value && pending.value.orderId === orderId) return pending.value
  return null
}

function updateOverlayCoordinates() {
  if (!visible.value || !props.ready || typeof props.priceToY !== 'function') {
    renderedRows.value = []
    return
  }

  const rows = []
  for (const cluster of clusters.value) {
    const override = orderOverride(cluster)
    const price = override ? override.price : cluster.price
    const qty = override ? override.qty : cluster.qty
    const y = props.priceToY(price)
    if (y == null) continue
    rows.push({
      ...cluster,
      orderId: cluster.orderIds.length === 1 ? cluster.orderIds[0] : null,
      draggable: cluster.orderIds.length === 1,
      price,
      qty,
      y,
      labelShift: 0,
    })
  }
  renderedRows.value = shiftOverlappingLabels(rows)
}

function showQtyEditor(row) {
  return row.draggable && (hoveredKey.value === row.key || focusedKey.value === row.key)
}

function onEnter(row) {
  hoveredKey.value = row.key
  if (focusedKey.value !== row.key && row.draggable) qtyDraft.value = qtyToDraft(row.qty)
}

function onLeave(event, row) {
  const next = event.relatedTarget
  if (next && event.currentTarget.parentElement?.contains(next)) return
  if (hoveredKey.value === row.key) hoveredKey.value = null
}

function qtyToDraft(qty) {
  const n = Number(qty)
  if (!Number.isFinite(n)) return ''
  return String(Math.round(n * 1e8) / 1e8)
}

function send(payload) {
  const socket = store.state.socket
  if (!socket || socket.readyState !== 1) return false
  socket.send(JSON.stringify(payload))
  return true
}

function submitEdit(orderId, price, qty) {
  const rounded = roundLimitPrice(price)
  const nextQty = Number(qty)
  if (!orderId || rounded == null || !Number.isFinite(nextQty) || nextQty <= 0) return
  const sent = send({ type: 'terminalEditOrder', orderId, price: rounded, qty: nextQty })
  if (!sent) return
  pending.value = {
    orderId,
    price: rounded,
    qty: nextQty,
    errorAtSubmit: store.state.terminalError || '',
  }
  clearTimeout(pendingTimer)
  pendingTimer = setTimeout(() => { pending.value = null }, PENDING_MS)
}

function confirmQty(row) {
  const qty = parseLimitQty(qtyDraft.value)
  if (qty == null || !row.orderId) return
  const price = row.price
  if (drag.value) endDrag(false)
  focusedKey.value = null
  const order = (store.state.terminalOpenOrders || []).find((item) => String(item.orderId) === row.orderId)
  const sourceQty = Number(order?.remainQty ?? order?.origQty)
  const sourcePrice = Number(order?.price)
  const unchanged = Math.abs(qty - sourceQty) < 1e-8 && Math.abs(price - sourcePrice) < 0.001
  const alreadySent = pending.value?.orderId === row.orderId
    && Math.abs(pending.value.qty - qty) < 1e-8
    && Math.abs(pending.value.price - price) < 0.001
  if (unchanged || alreadySent) return
  submitEdit(row.orderId, price, qty)
}

function onPointerDown(event, row) {
  if (drag.value || event.button !== 0 || !row.draggable) return
  if (event.target.closest('input, button')) return
  event.preventDefault()
  event.stopPropagation()
  drag.value = {
    orderId: row.orderId,
    price: row.price,
    qty: Number(row.qty),
    originPrice: row.price,
    moved: false,
  }
  window.addEventListener('pointermove', onPointerMove)
  window.addEventListener('pointerup', onPointerUp)
  window.addEventListener('keydown', onDragKey)
}

function onPointerMove(event) {
  if (!drag.value || !overlayRef.value || typeof props.yToPrice !== 'function') return
  const rect = overlayRef.value.getBoundingClientRect()
  const raw = props.yToPrice(event.clientY - rect.top)
  const price = roundLimitPrice(raw)
  if (price == null) return
  if (Math.abs(price - drag.value.originPrice) >= 0.001) drag.value.moved = true
  drag.value.price = price
}

function endDrag(commit) {
  window.removeEventListener('pointermove', onPointerMove)
  window.removeEventListener('pointerup', onPointerUp)
  window.removeEventListener('keydown', onDragKey)
  const current = drag.value
  drag.value = null
  if (!commit || !current?.moved) return
  if (Math.abs(current.price - current.originPrice) < 0.001) return
  submitEdit(current.orderId, current.price, current.qty)
}

function onPointerUp() {
  endDrag(true)
}

function onDragKey(event) {
  if (event.key === 'Escape') endDrag(false)
}

function reconcilePending() {
  if (!pending.value) return
  const orders = store.state.terminalOpenOrders || []
  const still = orders.some((order) => String(order.orderId) === pending.value.orderId)
  const error = store.state.terminalError || ''
  if (!still || (error && error !== pending.value.errorAtSubmit)) pending.value = null
}

let rafId = null
function startSyncLoop() {
  function loop() {
    if (visible.value && props.ready) updateOverlayCoordinates()
    rafId = requestAnimationFrame(loop)
  }
  loop()
}

function stopSyncLoop() {
  if (rafId) {
    cancelAnimationFrame(rafId)
    rafId = null
  }
}

watch([clusters, visible], updateOverlayCoordinates)
watch(() => [store.state.terminalOpenOrders, store.state.terminalError], reconcilePending)

onMounted(() => {
  startSyncLoop()
  window.addEventListener('keydown', handleKeyDown)
})

onUnmounted(() => {
  stopSyncLoop()
  endDrag(false)
  clearTimeout(pendingTimer)
  window.removeEventListener('keydown', handleKeyDown)
})
</script>

<style scoped>
.ol-btn {
  background: #21262d;
  color: #c9d1d9;
  border: 1px solid #30363d;
  border-radius: 6px;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
}

.ol-btn--active {
  background: rgba(63, 185, 80, 0.12);
  color: #3fb950;
  border-color: rgba(63, 185, 80, 0.55);
}

.ol-btn__hint {
  color: #8b949e;
  font-weight: 500;
}

.shortcut {
  color: #8b949e;
  font-weight: 400;
  font-size: 11px;
}

.open-limits__overlay {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 5;
  overflow: visible;
}

.ol-row {
  position: absolute;
  left: 0;
  right: 72px;
  height: 0;
  pointer-events: none;
}

.ol-row__line {
  position: absolute;
  left: 0;
  right: 0;
  top: -7px;
  height: 14px;
  pointer-events: auto;
  background: linear-gradient(
    to bottom,
    transparent 6px,
    currentColor 6px,
    currentColor 8px,
    transparent 8px
  );
}

.ol-row--draggable .ol-row__line,
.ol-row--draggable .ol-row__label {
  cursor: ns-resize;
}

.ol-row--dragging .ol-row__line {
  background: linear-gradient(
    to bottom,
    transparent 5px,
    currentColor 5px,
    currentColor 9px,
    transparent 9px
  );
}

.ol-row--buy {
  color: #3fb950;
}

.ol-row--sell {
  color: #f85149;
}

.ol-row__label {
  position: absolute;
  top: 0;
  right: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 2px 7px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.02em;
  white-space: nowrap;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8);
  pointer-events: auto;
}

.ol-qty {
  width: 64px;
  padding: 1px 4px;
  border: 1px solid currentColor;
  border-radius: 3px;
  background: #0d1117;
  color: inherit;
  font: inherit;
  font-weight: 600;
  cursor: text;
}

.ol-ok {
  padding: 1px 6px;
  border: 1px solid currentColor;
  border-radius: 3px;
  background: transparent;
  color: inherit;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}

.ol-row--buy .ol-row__label {
  color: #3fb950;
  background: rgba(13, 17, 23, 0.82);
  border: 1px solid rgba(63, 185, 80, 0.55);
}

.ol-row--sell .ol-row__label {
  color: #f85149;
  background: rgba(13, 17, 23, 0.82);
  border: 1px solid rgba(248, 81, 73, 0.55);
}
</style>
